/*
 * Sentry init config, externalised from the inline <head> block to cut parser cost.
 * Liquid-driven values are passed via window.__SENTRY_CFG__ (set inline in theme.liquid).
 * Loaded with `defer` BEFORE the Sentry loader script (also `defer`) so window.sentryOnLoad
 * is always defined before the loader invokes it.
 */
(function () {
  var cfg = window.__SENTRY_CFG__ || {};

  window.sentryOnLoad = function () {
    Sentry.init({
      dsn: "https://5d860ecd70b29896b04de3209ed9a5e1@o4511341161938944.ingest.us.sentry.io/4511341227737088",
      environment: cfg.environment,
      release: cfg.release,
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: 0.05,
      tracePropagationTargets: [
        "wearnala.com",
        "nala-wear-au.myshopify.com",
        /^\//,
      ],
      ignoreErrors: [
        // --- Network failures (user's connection) ---
        "Load failed",                          // WebKit / Safari / iOS phrasing
        "Failed to fetch",                      // Chrome / Firefox phrasing
        "Failed to load",
        "Failed to read",
        "Access is denied",
        "URIError",
        "URI malformed",
        "NetworkError when attempting to fetch resource", // Firefox
        "The network connection was lost",      // iOS
        "The Internet connection appears to be offline",  // iOS
        "cancelled",                            // iOS request cancellation
        "Network request failed",
        "TypeError: cancelled",
        "AbortError",                           // user navigated away mid-request

        // --- Browser extensions injecting code (not your bug) ---
        /extension\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        /^safari-web-extension:\/\//i,

        // --- Generic noise that's almost never actionable ---
        "Non-Error promise rejection captured",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Script error.",                        // cross-origin script with no details

        // --- iOS in-app browser quirks ---
        "InvalidStateError",                    // common in webviews
        "AbortError: The operation was aborted",

        // --- Third-party scripts misbehaving ---
        "fb_xd_fragment",                       // ancient Facebook SDK leftover
        "Can't find variable: _AutofillCallbackHandler",  // iOS Safari autofill
        "Cannot redefine property: ethereum",   // crypto wallet extensions
        "Cannot redefine property: solana",
        "instantSearchSDKJSBridgeClearHighlight", // Bing in-app browser injection
      ],
      beforeSendTransaction: function (event) {
        const name = event.transaction || "";
        const op = (event.contexts && event.contexts.trace && event.contexts.trace.op) || "";

        // 1. Drop Shopify cart polling — high volume, low signal
        if (name.includes("/cart.json") || name.includes("/cart/change") || name.includes("/cart/update")) {
          return null;
        }

        // 2. Drop Shopify section rendering API — fires constantly on theme interactions
        if (name.includes("/?sections=") || name.includes("&sections=")) {
          return null;
        }

        // 3. Drop search suggestions / predictive search — fires per keystroke
        if (name.includes("/search/suggest")) {
          return null;
        }

        // 4. Drop third-party analytics & marketing endpoints (we can't fix their perf anyway)
        const thirdPartyHosts = [
          "klaviyo.com",
          "skimresources.com",
          "visually.io",
          "loomi-prod",
          "yotpo.com",
          "google-analytics.com",
          "googletagmanager.com",
          "facebook.com",
          "facebook.net",
          "tiktok.com",
          "pinterest.com",
          "hotjar.com",
          "clarity.ms",
        ];
        if (thirdPartyHosts.some((host) => name.includes(host))) {
          return null;
        }

        // 5. Drop very short navigations (likely cancelled / bounced before meaningful work)
        const duration = (event.timestamp || 0) - (event.start_timestamp || 0);
        if (op === "navigation" && duration < 0.1) {
          return null;
        }

        return event;
      },
    });
    Sentry.setTag("template", cfg.template);
    Sentry.setTag("shop", cfg.shop);

    // Internal tester identification
    // Usage: append ?nala_test=<your_name> to any URL (e.g. ?nala_test=thao)
    // The tag persists across navigation within the same browser session.
    try {
      var params = new URLSearchParams(window.location.search);
      var testerFromUrl = params.get("nala_test");
      if (testerFromUrl) {
        sessionStorage.setItem("nala_test", testerFromUrl);
      }
      var tester = sessionStorage.getItem("nala_test");
      if (tester) {
        Sentry.setTag("nala_tester", tester);
        Sentry.setTag("internal_test", "true");
      }
    } catch (e) {
      // sessionStorage blocked — fall back to URL-only tagging
      var fallbackParams = new URLSearchParams(window.location.search);
      var fallbackTester = fallbackParams.get("nala_test");
      if (fallbackTester) {
        Sentry.setTag("nala_tester", fallbackTester);
        Sentry.setTag("internal_test", "true");
      }
    }

    // Identify the visitor
    if (cfg.customer) {
      Sentry.setUser({
        id: cfg.customer.id,
        email: cfg.customer.email,
        username: cfg.customer.username,
      });
    } else {
      try {
        var anonId = localStorage.getItem("nala_visitor_id");
        if (!anonId) {
          anonId = "anon-" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
          localStorage.setItem("nala_visitor_id", anonId);
        }
        Sentry.setUser({ id: anonId });
      } catch (e) {
        Sentry.setUser({ id: "anon-" + Math.random().toString(36).substring(2, 15) });
      }
    }
  };
})();
